/**
 * @file useShopCreateForm.ts
 * @description Состояние формы «Новый магазин» (phase19.28 этап А;
 * uiux-platform-shops §3; PL2, PL4, PL5, PL7).
 *
 * При вводе (с паузой 400 мс) сервер отвечает, свободен ли slug
 * (`GET slug-check` — «Свободен. Витрина: …» или «Адрес занят магазином …»)
 * и есть ли пользователь с почтой владельца (`GET owner-lookup` — найден,
 * роль платформы или «отправим приглашение»). Сбой этих подсказок форму не
 * блокирует: окончательно решает `POST /admin/platform/shops` (409 / 422
 * встают под своё поле).
 */

import { validateShopSlug } from "@chaiberi/shared";
import { useState } from "react";

import { platformShops, type CreateShopResult, type OwnerLookup, type ShopSlugCheck } from "../../lib/api-platform-shops";
import type { ShopTimeZoneOption } from "../../lib/api-shop-settings";
import {
  EMPTY_SHOP_CREATE,
  isEmail,
  lookupFor,
  toCreatePayload,
  validateShopCreate,
  type ShopCreateErrors,
  type ShopCreateValue,
} from "../../lib/platform/shop-create-form";
import { shopCreateError } from "../../lib/platform/shop-errors";
import { loadTimeZoneOptions } from "../../lib/platform/time-zone-options";
import { useDebounced } from "./useDebounced";
import { usePlatformResource } from "./usePlatformResource";

const CHECK_DELAY_MS = 400;

export interface ShopCreateFormState {
  value: ShopCreateValue;
  setValue: (next: ShopCreateValue) => void;
  errors: ShopCreateErrors;
  /** Ответ owner-lookup для текущей почты; null — ещё не проверили. */
  lookup: OwnerLookup | null;
  /** owner-lookup не ответил — не знаем, есть ли пользователь. */
  lookupUnavailable: boolean;
  /** Ответ slug-check для текущего slug; null — не проверяли (или формат неверен). */
  slugCheck: ShopSlugCheck | null;
  timeZones: ShopTimeZoneOption[];
  submitting: boolean;
  result: CreateShopResult | null;
  ownerMode: "existing" | "new" | null;
  submit: () => Promise<void>;
  reset: () => void;
}

const FIELD_OF: ReadonlyArray<[keyof ShopCreateValue, keyof ShopCreateErrors]> = [
  ["displayName", "displayName"],
  ["slug", "slug"],
  ["timeZone", "timeZone"],
  ["ownerEmail", "owner"],
  ["ownerName", "ownerName"],
  ["contactEmail", "contactEmail"],
];

export function useShopCreateForm(): ShopCreateFormState {
  const [value, setValueState] = useState<ShopCreateValue>(EMPTY_SHOP_CREATE);
  const [errors, setErrors] = useState<ShopCreateErrors>({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<CreateShopResult | null>(null);
  const [ownerMode, setOwnerMode] = useState<"existing" | "new" | null>(null);

  const slug = useDebounced(value.slug, CHECK_DELAY_MS);
  const email = useDebounced(value.ownerEmail.trim(), CHECK_DELAY_MS);
  const slugKey = slug && validateShopSlug(slug).data ? `slug-check?${slug}` : null;
  const emailKey = isEmail(email) ? `owner-lookup?${email.toLowerCase()}` : null;
  const slugRes = usePlatformResource(slugKey, () => platformShops.slugCheck(slug), "");
  const ownerRes = usePlatformResource(emailKey, () => platformShops.ownerLookup(email), "Поиск владельца недоступен");
  const zones = usePlatformResource("time-zones", loadTimeZoneOptions, "Не удалось загрузить часовые пояса");

  const lookup = lookupFor(ownerRes.data, value.ownerEmail);
  const slugCheck = slugRes.data && slugRes.data.slug === value.slug ? slugRes.data : null;

  function setValue(next: ShopCreateValue) {
    // Поле поправили — его ошибка больше не про текущее значение.
    setErrors((prev) => {
      const kept: ShopCreateErrors = { ...prev };
      for (const [key, field] of FIELD_OF) if (next[key] !== value[key]) delete kept[field];
      delete kept.form;
      return kept;
    });
    setValueState(next);
  }

  async function submit() {
    const found = validateShopCreate(value, lookup);
    setErrors(found);
    if (Object.keys(found).length > 0) return;
    setSubmitting(true);
    try {
      const created = await platformShops.create(toCreatePayload(value, lookup));
      setOwnerMode(lookup?.user ? "existing" : "new");
      setResult(created);
    } catch (err) {
      const { field, message } = shopCreateError(err);
      setErrors({ [field]: message });
    } finally {
      setSubmitting(false);
    }
  }

  function reset() {
    setValueState(EMPTY_SHOP_CREATE);
    setErrors({});
    setResult(null);
    setOwnerMode(null);
  }

  return {
    value,
    setValue,
    errors,
    lookup,
    lookupUnavailable: !!ownerRes.error,
    slugCheck,
    timeZones: zones.data ?? [],
    submitting,
    result,
    ownerMode,
    submit,
    reset,
  };
}
